import { useEffect, useState } from 'react'
import { SignedIn, SignedOut, SignIn, SignUp } from '@clerk/clerk-react'
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import {
  AccountsRail,
  Button,
  CmdK,
  ConfirmDialogHost,
  PageHead,
  Toast,
  TopBar,
  type NavId,
} from '../components'
import { CashgapBanner } from '../components/CashgapBanner'
import { UpdateBanner } from '../components/update-banner'
import { T } from '../data/mock'
import { AppStateProvider, useAppState } from '../app-state'
import { ActiveOrgProvider } from '../app-state/active-org'
import { TransactionFormDrawer } from '../screens/transaction-form'

const PATH_TO_NAV: Record<string, NavId> = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/transactions': 'transactions',
  '/forecast': 'forecast',
  '/cashflow': 'cashflow',
  '/salaries': 'salaries',
  '/planning': 'planning',
  '/licenses': 'licenses',
  '/invoices': 'invoices',
  '/settings': 'settings',
}

const navToPath = (id: NavId | 'accounts') =>
  id === 'accounts' ? '/transactions' : `/${id === 'dashboard' ? '' : id}`

const TITLES: Record<NavId, string> = {
  dashboard: T.nav.dashboard,
  transactions: T.nav.transactions,
  forecast: T.nav.forecast,
  cashflow: T.nav.cashflow,
  salaries: T.nav.salaries,
  planning: 'Планування',
  licenses: T.nav.licenses,
  invoices: 'Інвойси',
  settings: T.nav.settings,
}

// Breadcrumbs are per-screen; none of the current ones carry a second level.
const CRUMBS: Partial<Record<NavId, string>> = {}

function PrimaryAction({ active }: { active: NavId }) {
  const { openNewTx } = useAppState()

  if (active === 'forecast')
    return (
      <Button variant="primary" size="sm" icon="refresh-cw">
        {T.buttons.recalc}
      </Button>
    )
  if (active === 'cashflow') return null
  if (active === 'salaries') return null
  if (active === 'planning') return null
  if (active === 'licenses')
    return (
      <Button variant="primary" size="sm" icon="plus">
        {T.buttons.new_license}
      </Button>
    )
  if (active === 'settings') return null
  return (
    <Button variant="primary" size="sm" icon="plus" kbd="N" onClick={openNewTx}>
      {T.buttons.new_tx}
    </Button>
  )
}

function AppShell() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const active: NavId =
    pathname.startsWith('/movement/') ? 'transactions' : (PATH_TO_NAV[pathname] ?? 'dashboard')
  const {
    railSelected,
    setRailSelected,
    cmdkOpen,
    setCmdkOpen,
    drawerTx,
    setDrawerTx,
    toast,
    openNewTx,
  } = useAppState()
  // Off-canvas nav/accounts panel on phones; never opens on desktop (the
  // hamburger that toggles it is hidden ≥769px).
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = (target?.tagName ?? '').toLowerCase()
      const isTyping =
        tag === 'input' || tag === 'textarea' || (target?.isContentEditable ?? false)

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCmdkOpen(true)
        return
      }
      if (isTyping) return

      if (e.key.toLowerCase() === 'n') {
        e.preventDefault()
        openNewTx()
        void navigate({ to: '/transactions' })
      } else if (e.key === '/') {
        e.preventDefault()
        const inp = document.querySelector<HTMLInputElement>('.search input')
        inp?.focus()
      } else if (e.key === 'Escape') {
        if (cmdkOpen) setCmdkOpen(false)
        if (drawerTx) setDrawerTx(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [cmdkOpen, drawerTx, openNewTx, setCmdkOpen, setDrawerTx, navigate])

  const handleNavigate = (id: NavId) => {
    setDrawerTx(null)
    setMobileNavOpen(false)
    void navigate({ to: navToPath(id) })
  }

  const handleRailSelect = (id: string) => {
    setRailSelected(id)
    setMobileNavOpen(false)
    // /movement/:id is locked to a single account's statement, so ANY rail
    // change — picking another account OR switching to "Усі рахунки" — must
    // leave that now-stale page. This has to run before the `id === 'all'`
    // shortcut below, otherwise "Усі рахунки" would strand you on the
    // statement with no way back out.
    if (pathname.startsWith('/movement/')) {
      void navigate({ to: '/transactions' })
      return
    }
    if (id === 'all') return
    // From a page that doesn't honour the account filter, scoping to a specific
    // account jumps to /transactions so the new filter actually takes effect.
    if (active !== 'transactions' && active !== 'forecast' && active !== 'cashflow') {
      void navigate({ to: '/transactions' })
    }
  }

  return (
    <div className="app">
      <UpdateBanner />
      <CashgapBanner />
      <TopBar
        active={active}
        onNavigate={handleNavigate}
        onCmdK={() => setCmdkOpen(true)}
        onMenuToggle={() => setMobileNavOpen((v) => !v)}
      />
      <div className={`app-body ${mobileNavOpen ? 'mobile-open' : ''}`}>
        {mobileNavOpen && (
          <div className="rail-scrim" onClick={() => setMobileNavOpen(false)} />
        )}
        <AccountsRail
          active={active}
          selected={railSelected}
          onSelect={handleRailSelect}
          onNavigate={handleNavigate}
          onOpenMovement={(id) => {
            setMobileNavOpen(false)
            void navigate({ to: '/movement/$accountId', params: { accountId: id } })
          }}
        />
        <main className="main">
          <PageHead
            title={TITLES[active]}
            crumb={CRUMBS[active]}
            primary={<PrimaryAction active={active} />}
          />
          <Outlet />
        </main>
      </div>

      {cmdkOpen && (
        <CmdK
          onClose={() => setCmdkOpen(false)}
          onNavigate={(id) => {
            setCmdkOpen(false)
            setDrawerTx(null)
            void navigate({ to: navToPath(id) })
          }}
          onAction={(id) => {
            if (id === 'new-tx') {
              openNewTx()
              void navigate({ to: '/transactions' })
            }
          }}
        />
      )}

      {drawerTx && (
        <TransactionFormDrawer drawerTx={drawerTx} onClose={() => setDrawerTx(null)} />
      )}

      <Toast message={toast} />
      <ConfirmDialogHost />
    </div>
  )
}

function SignInGate() {
  // Clerk invitation links arrive with a `__clerk_ticket` query param. The
  // prebuilt <SignUp> consumes it (the invited email is pre-verified) and, with
  // signUpFallbackRedirectUrl="/", drops the new user into the app — instead of
  // stranding them on Clerk's hosted Account Portal. Everyone else signs in.
  const hasInviteTicket = new URLSearchParams(window.location.search).has('__clerk_ticket')
  return (
    <div
      style={{
        height: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg-0)',
      }}
    >
      {hasInviteTicket ? <SignUp routing="hash" /> : <SignIn routing="hash" />}
    </div>
  )
}

export const Route = createRootRoute({
  component: () => (
    <>
      <SignedIn>
        <ActiveOrgProvider>
          <AppStateProvider>
            <AppShell />
          </AppStateProvider>
        </ActiveOrgProvider>
      </SignedIn>
      <SignedOut>
        <SignInGate />
      </SignedOut>
    </>
  ),
})
